import { Caption1, Menu, MenuDivider, MenuItem, MenuList, MenuPopover, MenuTrigger, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { Add16Regular, LinkDismiss16Regular, MoreHorizontal16Regular, Open16Regular } from "@fluentui/react-icons";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FuiButton } from "@/components/fui-control";
import { useCan } from "@/features/access/use-plan-access";
import type { DocumentLinkTargetKind, PlanDocument } from "@/engine/types";
import { RefSearchAdd } from "@/features/records/record-ui";
import { useProjectStore } from "@/store/project-store";
import { DOCUMENT_KIND_LABEL, documentCode, documentTitle } from "./doc-model";
import { documentNoticeCount } from "./document-ops";
import { documentsForTarget, linkContext, tombstoneSentence } from "./doc-links";
import { linkedExecutionIds, useDocReader } from "./doc-reader";
import { canEditDocument, canReadDocument } from "./doc-visibility";

const MORE_CLASS = "doc-link-more";

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    margin: 0,
    padding: 0,
    listStyleType: "none",
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXXS,
    minWidth: 0,
    minHeight: "32px",
    borderRadius: tokens.borderRadiusMedium,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    [`:hover .${MORE_CLASS}`]: { opacity: 1 },
    [`:focus-within .${MORE_CLASS}`]: { opacity: 1 },
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    flexGrow: 1,
    minWidth: 0,
    alignSelf: "stretch",
    padding: "2px 4px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    cursor: "pointer",
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  more: { opacity: 0, "@media (hover: none)": { opacity: 1 } },
  moreOpen: { opacity: 1 },
  quiet: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    width: "100%",
    minHeight: "32px",
    padding: `0 ${tokens.spacingHorizontalS}`,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    font: "inherit",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover, color: tokens.colorNeutralForeground2 },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  addIcon: { display: "inline-flex", width: "20px", justifyContent: "center", color: tokens.colorBrandForeground1 },
  code: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
    fontVariantNumeric: "tabular-nums",
  },
  title: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  text: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flexGrow: 1,
  },
  context: {
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  muted: { color: tokens.colorNeutralForeground3 },
});

export function useTombstoneSentence(kind: DocumentLinkTargetKind, ids: readonly string[]): string | null {
  const project = useProjectStore((s) => s.project);
  const reader = useDocReader();
  return useMemo(
    () => tombstoneSentence(documentNoticeCount(project, kind, ids, (doc) => canReadDocument(doc, reader))),
    [project, kind, ids, reader],
  );
}

/** 작업·이슈·일정 상세의 「문서」절. 고칠 수 있으면 기존 글을 연결하고, 그 연결만 끊는다. */
export function LinkedDocumentRows({ kind, targetId, canEdit = false }: { kind: DocumentLinkTargetKind; targetId: string; canEdit?: boolean }) {
  const styles = useStyles();
  const navigate = useNavigate();
  const project = useProjectStore((s) => s.project);
  const projectId = useProjectStore((s) => s.activeId);
  const reader = useDocReader();
  const mayEditDocs = useCan("plan.doc.edit");
  const canManage = canEdit && mayEditDocs;
  const [adding, setAdding] = useState(false);
  const docs = useMemo(
    () => documentsForTarget(project.documents, project.documentLinks, kind, targetId, (doc) => canReadDocument(doc, reader)),
    [project.documents, project.documentLinks, kind, targetId, reader],
  );
  const options = useMemo(() => {
    const linked = new Set(docs.map((doc) => doc.id));
    return project.documents
      .filter((doc) => !doc.deletedAt && !linked.has(doc.id) && canEditDocument(doc, reader, linkedExecutionIds(doc.id, project.documentLinks)))
      .map((doc) => ({
        id: doc.id,
        code: documentCode(doc.seq),
        label: documentTitle(doc.title),
        sub: DOCUMENT_KIND_LABEL[doc.kind],
      }));
  }, [docs, project.documents, project.documentLinks, reader]);

  const openDoc = (docId: string) => {
    useProjectStore.getState().setSchedulePeek(null);
    if (projectId) navigate(`/projects/${projectId}/docs/${docId}`);
  };

  if (!docs.length && !canManage) return <Caption1 className={styles.muted}>0</Caption1>;
  return (
    <div>
      {docs.length > 0 && (
        <ul className={styles.list}>
          {docs.map((doc) => {
            const link = project.documentLinks.find((item) => item.documentId === doc.id && item.targetKind === kind && item.targetId === targetId);
            const context = link ? linkContext(doc.blocks, link) : null;
            const where = context ? [context.heading, context.sentence].filter(Boolean).join(" · ") : "";
            const canUnlink = canManage && !!link && canEditDocument(doc, reader, linkedExecutionIds(doc.id, project.documentLinks));
            return (
              <DocumentRow
                key={doc.id}
                doc={doc}
                where={where}
                canUnlink={canUnlink}
                onOpen={() => openDoc(doc.id)}
                onUnlink={link ? () => useProjectStore.getState().unlinkDocument(doc.id, link.id) : undefined}
              />
            );
          })}
        </ul>
      )}
      {canManage &&
        (adding ? (
          <RefSearchAdd
            autoFocus
            exclude={docs.map((doc) => doc.id)}
            options={options}
            onPick={(id) => useProjectStore.getState().linkDocument(id, kind, targetId)}
            onDismiss={() => setAdding(false)}
            placeholder="문서 번호 또는 제목"
            ariaLabel="문서 연결"
          />
        ) : (
          <button type="button" className={styles.quiet} onClick={() => setAdding(true)}>
            <span className={styles.addIcon}>
              <Add16Regular />
            </span>
            문서 연결
          </button>
        ))}
    </div>
  );
}

function DocumentRow({
  doc,
  where,
  canUnlink,
  onOpen,
  onUnlink,
}: {
  doc: PlanDocument;
  where: string;
  canUnlink: boolean;
  onOpen: () => void;
  onUnlink?: () => void;
}) {
  const styles = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const code = documentCode(doc.seq);
  const title = documentTitle(doc.title);
  return (
    <li
      className={styles.item}
      onContextMenu={(event) => {
        if (!canUnlink) return;
        event.preventDefault();
        setMenuOpen(true);
      }}
    >
      <button type="button" className={styles.row} onClick={onOpen} title={title}>
        <span className={styles.code}>{code}</span>
        <span className={styles.text}>
          <span className={styles.title}>{title}</span>
          {where ? <Caption1 className={styles.context}>{where}</Caption1> : null}
        </span>
      </button>
      {canUnlink && onUnlink ? (
        <Menu open={menuOpen} onOpenChange={(_, data) => setMenuOpen(data.open)} positioning="below-end">
          <MenuTrigger disableButtonEnhancement>
            <FuiButton
              className={mergeClasses(MORE_CLASS, styles.more, menuOpen && styles.moreOpen)}
              appearance="subtle"
              density="dialog"
              icon={<MoreHorizontal16Regular />}
              aria-label={`${code} ${title} 동작`}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<Open16Regular />} onClick={onOpen}>
                열기
              </MenuItem>
              <MenuDivider />
              <MenuItem icon={<LinkDismiss16Regular />} onClick={onUnlink}>
                연결 해제
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      ) : null}
    </li>
  );
}
